import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';
import {
  deletePartnershipSubmission,
  getPartnershipSubmissionById,
} from '../api/partnershipSubmissionsApi';
import ConfirmDialog from '../components/dashboard/ConfirmDialog';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const formatUrl = (value) => {
  if (!value) {
    return null;
  }

  return value.includes('://') ? value : `https://${value}`;
};

const DetailRow = ({ label, value, isLink = false }) => (
  <div className="border-b border-[#f0e8dc] py-3 last:border-b-0">
    <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-[#9a9288]">{label}</p>
    {isLink && value ? (
      <a
        href={formatUrl(value)}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex text-sm text-[#785E32] hover:underline"
      >
        {value}
      </a>
    ) : (
      <p className="mt-1 whitespace-pre-wrap text-sm text-[#130705]">{value || '—'}</p>
    )}
  </div>
);

const PartnershipSubmissionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const data = await getPartnershipSubmissionById(id);
        if (!isMounted) {
          return;
        }

        setSubmission(data);
        setLoadError('');
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setSubmission(null);
        setLoadError(err.message || 'Failed to load submission.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleDeleteClick = () => {
    setDeleteError('');
    setIsConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    if (isDeleting) {
      return;
    }

    setIsConfirmOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!submission) {
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      await deletePartnershipSubmission(submission.id);
      navigate('/dashboard/partnership-submissions');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete submission.');
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading submission…</p>;
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadError}
        </p>
        <Link
          to="/dashboard/partnership-submissions"
          className="inline-flex text-sm font-medium text-[#785E32] hover:underline"
        >
          Back to submissions
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <Link
          to="/dashboard/partnership-submissions"
          className="text-[12px] font-medium text-[#785E32] hover:underline"
        >
          ← All submissions
        </Link>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
              Inquiry #{submission.id}
            </p>
            <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
              {submission.fullName}
            </h2>
            <p className="mt-2 text-sm text-[#6b635c]">
              Submitted {formatDate(submission.createdAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="inline-flex shrink-0 items-center gap-2 rounded-md !border !border-[#e4d9c8] !bg-white px-3 py-2 text-[13px] font-medium !text-[#6b635c] !shadow-none transition hover:!border-[#fecdca] hover:!bg-[#fef3f2] hover:!text-[#b42318] hover:!translate-y-0 hover:!shadow-none disabled:opacity-60"
          >
            <FiTrash2 className="h-4 w-4" aria-hidden="true" />
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        {deleteError ? (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {deleteError}
          </p>
        ) : null}
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-md border border-[#e4d9c8] bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-[#130705]">Applicant details</h3>
          <DetailRow label="First name" value={submission.firstName} />
          <DetailRow label="Last name" value={submission.lastName} />
          <DetailRow label="Email" value={submission.email} />
          <DetailRow
            label="Phone"
            value={`${submission.countryCode} ${submission.phone}`}
          />
          <DetailRow label="City" value={submission.city} />
          <DetailRow label="Country" value={submission.country} />
        </section>

        <section className="rounded-md border border-[#e4d9c8] bg-white p-5">
          <h3 className="mb-3 text-sm font-semibold text-[#130705]">Business details</h3>
          <DetailRow label="Website" value={submission.website} isLink />
          <DetailRow label="Social media" value={submission.socialMedia} isLink />
          <DetailRow label="Submitted" value={formatDate(submission.createdAt)} />
        </section>
      </div>

      <section className="mt-6 rounded-md border border-[#e4d9c8] bg-white p-5">
        <h3 className="mb-3 text-sm font-semibold text-[#130705]">Message</h3>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#130705]">
          {submission.message}
        </p>
      </section>

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete submission?"
        message={`Delete the inquiry from ${submission.fullName}? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default PartnershipSubmissionDetailPage;
